import type { RecruitBoardConfig, RecruitListItem } from './RecruitTypes';

export function renderPreview30(content: string) {
  if (content?.length > 30) return `${content.substring(0, 30)}...`;
  return content;
}

export function renderPreview50(content: string) {
  if (content?.length > 50) return `${content.substring(0, 50)}...`;
  return content;
}

export function highlightSearchText(text: string, searchTerms: string[]) {
  if (!searchTerms?.length || !text) return text;

  let highlightedText = text;
  searchTerms.forEach((term) => {
    if (term?.trim()) {
      const regex = new RegExp(`(${term})`, 'gi');
      highlightedText = highlightedText.replace(
        regex,
        '<span style="color:rgb(30, 0, 199); font-weight: bold;">$1</span>',
      );
    }
  });
  return highlightedText;
}

export function safeJsonParse<T>(raw: unknown, fallback: T): T {
  if (raw == null || raw === '') return fallback;
  if (typeof raw === 'object') return raw as T;
  try {
    return JSON.parse(String(raw)) as T;
  } catch {
    return fallback;
  }
}

/** sort 필드가 있는 리스트 항목 — 항목이 2개 이상일 때만 "전임 : " 형태로 노출 */
export function sortLabelPrefix(
  items: unknown[] | null | undefined,
  sort?: string,
): string {
  if (!items || items.length <= 1 || !sort) return '';
  return `${sort} : `;
}

export function matchesSortFilter(
  item: { sort?: string; title?: string },
  selectedSort: string[],
  config: RecruitBoardConfig,
) {
  if (selectedSort.length === 0) return true;
  const field = config.sortFilterField === 'title' ? item.title : item.sort;
  return selectedSort.some((s) => field && field.includes(s));
}

export function matchesLocationFilter(
  item: { location?: string },
  selectedLocation: string[],
) {
  if (selectedLocation.length === 0) return true;
  return selectedLocation.some(
    (selectedLoc) =>
      item.location === selectedLoc ||
      (item.location && item.location.includes(selectedLoc)),
  );
}

export function matchesReligiousbodyFilter(
  item: { religiousbody?: string },
  selectedReligiousbody: string[],
) {
  if (selectedReligiousbody.length === 0) return true;
  return selectedReligiousbody.includes(item.religiousbody || '');
}

// 직분·사례비 필터 — 표시 라벨 ↔ 서버 정형 필드 코드 (server/routers/seo/recruitSeoFields.js)
export const POSITION_FILTER_OPTIONS: Record<string, string> = {
  담임목사: 'SENIOR',
  부목사: 'ASSOCIATE',
  전임전도사: 'FULL_EVANGELIST',
  교육전도사: 'EDU_EVANGELIST',
  파트전도사: 'PART_EVANGELIST',
  찬양사역자: 'WORSHIP_LEADER',
  반주자: 'ACCOMPANIST',
  선교사: 'MISSIONARY',
  행정간사: 'ADMIN_STAFF',
};

/** 사례비 구간 (월 기준, 연봉은 12로 나눠 계산된 payBand) — '협의·내규'는 payType으로 판정 */
export const PAY_FILTER_OPTIONS: Record<string, string> = {
  '100만원 미만': 'B0',
  '100~150만원': 'B1',
  '150~200만원': 'B2',
  '200~250만원': 'B3',
  '250~300만원': 'B4',
  '300~400만원': 'B5',
  '400만원 이상': 'B6',
  '협의·내규': 'NEGOTIABLE',
};

export function matchesPositionFilter(item: { jobPosition?: string | null }, selected: string[]) {
  if (selected.length === 0) return true;
  return selected.some((label) => POSITION_FILTER_OPTIONS[label] === item.jobPosition);
}

export function matchesPayFilter(item: { payBand?: string | null; payType?: string | null }, selected: string[]) {
  if (selected.length === 0) return true;
  return selected.some((label) => {
    const code = PAY_FILTER_OPTIONS[label];
    if (code === 'NEGOTIABLE') return item.payType === 'NEGOTIABLE' || item.payType === 'CHURCH_RULE';
    return item.payBand === code;
  });
}

export function filterRecruitItems(
  data: RecruitListItem[],
  selectedSort: string[],
  selectedLocation: string[],
  selectedReligiousbody: string[],
  config: RecruitBoardConfig,
  selectedPosition: string[] = [],
  selectedPay: string[] = [],
): RecruitListItem[] {
  return data.filter(
    (item) =>
      matchesSortFilter(item, selectedSort, config) &&
      matchesLocationFilter(item, selectedLocation) &&
      matchesReligiousbodyFilter(item, selectedReligiousbody) &&
      matchesPositionFilter(item, selectedPosition) &&
      matchesPayFilter(item, selectedPay),
  );
}

// ---------------------------------------------------------------------------------
// 목록 필터 → 공고 알림 조건 (server/routers/alerts/jobAlerts.js 코드)
// ---------------------------------------------------------------------------------
const SIDO_CODE_BY_NAME: Record<string, string> = {
  서울특별시: '11', 부산광역시: '26', 대구광역시: '27', 인천광역시: '28', 광주광역시: '29', 대전광역시: '30', 울산광역시: '31',
  세종특별자치시: '36', 경기도: '41', 강원특별자치도: '51', 충청북도: '43', 충청남도: '44', 전북특별자치도: '52', 전라남도: '46',
  경상북도: '47', 경상남도: '48', 제주특별자치도: '50',
};
const EMPLOYMENT_CODE_BY_SORT: Record<string, string> = { 전임: 'FULL_TIME', 준전임: 'SEMI_FULL', 파트: 'PART_TIME' };

export function filtersToAlertDraft(
  tableType: string,
  { sort, location, position, pay }: { sort: string[]; location: string[]; position: string[]; pay: string[] },
) {
  const regions = tableType === 'overseas'
    ? ['OVERSEAS']
    : location.map((l) => SIDO_CODE_BY_NAME[l]).filter(Boolean);
  const positions = [
    ...position.map((p) => POSITION_FILTER_OPTIONS[p]).filter(Boolean),
    ...(sort.includes('담임') ? ['SENIOR'] : []),
  ];
  return {
    name: '',
    regions: Array.from(new Set(regions)),
    positions: Array.from(new Set(positions)),
    payBands: pay.map((p) => PAY_FILTER_OPTIONS[p]).filter(Boolean),
    employment: sort.map((x) => EMPLOYMENT_CODE_BY_SORT[x]).filter(Boolean),
  };
}

/** 공고 목록 정렬 — 공고 날짜 최신순, 같은 날이면 나중에 올린 글 먼저 (server routers/recruit/recruitOrder.js 와 같은 기준) */
const recruitDateValue = (date: unknown) => {
  const m = String(date || '').match(/(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})/);
  return m ? Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]) : -1;
};
export function compareRecruitByDate(a: { date?: unknown; id?: unknown }, b: { date?: unknown; id?: unknown }) {
  return recruitDateValue(b.date) - recruitDateValue(a.date) || Number(b.id) - Number(a.id);
}
